import * as THREE from 'three'
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

/**
 * 視点の抽象化。
 * - PC: 従来どおりカメラ位置 + OrbitControls ターゲットを直接設定（現行挙動と互換）
 * - XR: リグ（カメラの親）を移動し、向きはヨーのみ。頭の向きはHMDに任せる
 * プロトは camera を直接触らず、必ずこのクラス経由で視点を動かす。
 */
export class ViewRig {
  readonly rig = new THREE.Group()
  xrActive = false
  /** 無操作時のシネマティックドリフト（PCのみ）。演出がレールカメラを持つプロトでは自動的に上書きされる */
  driftEnabled = true
  private idleT = 0

  constructor(
    private camera: THREE.PerspectiveCamera,
    private controls: OrbitControls,
  ) {
    this.rig.add(camera)
    // 操作でドリフトを一時停止
    const mark = (): void => { this.idleT = 0 }
    window.addEventListener('pointerdown', mark)
    window.addEventListener('wheel', mark, { passive: true })
    window.addEventListener('keydown', mark)
  }

  /** 毎フレーム呼ぶ。5秒無操作で注視点のまわりをゆっくり回り込む（プロっぽい寄り引きの代わりの定常ムーブ） */
  update(dt: number): void {
    if (this.xrActive || !this.driftEnabled) return
    this.idleT += dt
    if (this.idleT < 5) return
    const t = this.controls.target
    const vx = this.camera.position.x - t.x
    const vz = this.camera.position.z - t.z
    const ang = dt * 0.035
    const cos = Math.cos(ang)
    const sin = Math.sin(ang)
    this.camera.position.x = t.x + vx * cos - vz * sin
    this.camera.position.z = t.z + vx * sin + vz * cos
  }

  /** 視点を置く。pos=目の位置(PC)/足元(XR)、look=注視点 */
  setPose(pos: THREE.Vector3, look: THREE.Vector3): void {
    if (this.xrActive) {
      this.rig.position.copy(pos)
      const dx = look.x - pos.x
      const dz = look.z - pos.z
      if (dx * dx + dz * dz > 1e-6) this.rig.rotation.set(0, Math.atan2(dx, dz) + Math.PI, 0)
    } else {
      this.camera.position.copy(pos)
      this.controls.target.copy(look)
    }
  }

  /** リグ/カメラを初期状態へ */
  reset(defaultCam: THREE.Vector3, defaultTgt: THREE.Vector3, xrStart: THREE.Vector3): void {
    this.rig.position.set(0, 0, 0)
    this.rig.rotation.set(0, 0, 0)
    if (this.xrActive) {
      this.rig.position.copy(xrStart)
    } else {
      this.camera.position.copy(defaultCam)
      this.controls.target.copy(defaultTgt)
    }
  }

  /** 現在の視点位置（ニアミス判定等に） */
  eyePosition(out: THREE.Vector3): THREE.Vector3 {
    return this.camera.getWorldPosition(out)
  }
}
